// tslint:disable-next-line: ban-comma-operator
import { Body, Button, palettes } from '@indigo-ag/component-module';
import { AuthService, Logger, login } from '@indigo-ag/ui-core';
import i18next from 'core/i18n';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { createStylesHook } from 'shared';
import { localStorageUtils } from 'shared/utils';
import { AuthCallbackMessageType, SIGNUP_FIELDS_MAP, SignUpFields } from './types';

// In certain workflows (eg. email verification) Auth0 will redirect (callback) to our login page with specific
// messages to indicate the success or failure of some action. These messages are unfortunately not customizable.
// As a result we're maintaining a map of the default message text to it's message type. This map is consulted
// when performing message substitutions within the Login component.
const AUTH_CALLBACK_MESSAGE_TYPE_MAP = {
  'Your email was verified. You can continue using the application.': AuthCallbackMessageType.EMAIL_VERIFIED
};

const useStyles = createStylesHook({
  errorWrapper: {
    '& button': {
      width: '30%',
      marginTop: 16
    },
    '& p': {
      color: palettes.neutral[700]
    },
    backgroundColor: palettes.neutral.white,
    borderRadius: 6,
    padding: 16,
    textAlign: 'center',
    width: 300
  }
});

interface LoginComponentProps {
  onAuthSuccess: () => void;
}

const LoginComponent: React.FunctionComponent<LoginComponentProps> = ({ onAuthSuccess }) => {
  /**
   * State
   */
  const [authenticateSuccess, setAuthenticateSuccess] = useState(false);
  const [hasError, setHasError] = useState(false);

  /**
   * Store
   */
  const dispatch = useDispatch();

  /**
   * Styles
   */
  const classes = useStyles();

  const callbackMessageSubstitutions: { [key in AuthCallbackMessageType]?: string } = {};

  const propsToAuth0Options = (): Auth0LockConstructorOptions => {
    const auth0Options: Partial<Auth0LockConstructorOptions> = {
      additionalSignUpFields: [
        SIGNUP_FIELDS_MAP[SignUpFields.firstName],
        SIGNUP_FIELDS_MAP[SignUpFields.lastName],
        SIGNUP_FIELDS_MAP[SignUpFields.phone]
      ],
      allowLogin: true,
      allowSignUp: false,
      initialScreen: 'login',
      mustAcceptTerms: true
    };

    return auth0Options;
  };

  const lock = AuthService.getLock(propsToAuth0Options());

  useEffect(() => {
    lockSetUp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasError && authenticateSuccess) {
      localStorageUtils.clearFinancingProcessFilterCriteria();
      onAuthSuccess();
    }
  }, [authenticateSuccess, hasError]); // eslint-disable-line react-hooks/exhaustive-deps

  const getQueryStringValue = (key: string) => {
    return decodeURIComponent(
      window.location.search.replace(
        new RegExp('^(?:.*[&\\?]' + encodeURIComponent(key).replace(/[.+*]/g, '\\$&') + '(?:\\=([^&]*))?)?.*$', 'i'),
        '$1'
      )
    );
  };

  const getCallbackMessageSubstitution = (text: string) => {
    const callbackMessageType = AUTH_CALLBACK_MESSAGE_TYPE_MAP[text];
    return (callbackMessageType && callbackMessageSubstitutions[callbackMessageType]) || text;
  };

  const lockSetUp = () => {
    // NOTE: Formerly we were calling `showLogin` directly, but this overrides the display of flash messages that result
    // from handling 'authorization_error' events. See: https://github.com/auth0/lock/issues/692#issuecomment-257495984
    lock.on('hash_parsed', hash => {
      // There is no hash, meaning this is the user's first visit, i.e. the authenticated or error events haven't fired
      if (!hash) {
        showLogin();
      }
    });

    lock.on('authenticated', ({ idToken, accessToken }) => {
      dispatch(login({ idToken, accessToken }));
      hideLogin();
      setAuthenticateSuccess(true);
    });

    // Authorization error (login failed)
    lock.on('authorization_error', error => {
      Logger.error('authorization_error', error);
      lock.show({
        flashMessage: {
          text: error.errorDescription || 'You are not authorized for this application',
          type: 'error'
        }
      });
    });

    // Unrecoverable error (no connection)
    lock.on('unrecoverable_error', error => {
      Logger.error('unrecoverable_error', error);
      hideLogin();
      setHasError(true);
    });
  };

  const showLogin = () => {
    const text = getCallbackMessageSubstitution(getQueryStringValue('message'));
    if (text) {
      const type = getQueryStringValue('success') === 'true' ? 'success' : 'error';
      const flashMessage: Auth0LockFlashMessageOptions = { type, text };
      lock.show({ flashMessage });
    } else {
      lock.show();
    }
  };

  const hideLogin = () => {
    lock.hide();
  };

  // TODO: find out in which case we want to show login again otherwise remove OK button
  const clearError = () => {
    setHasError(false);
    if (lock) {
      showLogin();
    }
  };

  return !hasError && authenticateSuccess ? null : hasError ? (
    <div className={`${classes.errorWrapper} flex flex-column align-items-center`}>
      <Body>{i18next.t('authAlertMessage')}</Body>
      <Button onClick={clearError} size="small">
        Ok
      </Button>
    </div>
  ) : (
    <div id="login" />
  );
};

export { LoginComponent as Login };
